import { useState } from 'react';
import { createPortal } from 'react-dom';
import {
	Alert,
	Button,
	Drawer,
	Empty,
	Space,
	Spin,
	Switch,
	Tag,
	Timeline,
	Tooltip,
	Typography,
} from 'antd';
import { EnvironmentOutlined, PrinterOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';

import {
	MaterialLengthHistory,
	useGetMaterialLengthHistoryQuery,
} from '../../redux/api/material-movements';
import { type LocationLabelFormatter, useLocationLabel } from '../../utils/locationLabel';
import ActionTag from './ActionTag';
import {
	actionLabel,
	formatInches,
	formatLengthChange,
	formatLocationChange,
	formatShopTime,
	operatorName,
	timelineColor,
} from './movementMeta';
import './history-print.css';

const { Text } = Typography;

const PRINT_MODE_CLASS = 'material-history-print-mode';
const PRINT_PAGE_RULE = '@page { size: portrait; margin: 0.5in; }';

interface MaterialHistoryDrawerProps {
	materialLengthId: string | null;
	friendlyId?: string;
	open: boolean;
	onClose: () => void;
}

/**
 * The page's own print mode (if any) is overridden for the duration of this
 * print, then restored, so printing a bar's history from inside the
 * Material Movements report prints the history rather than the report.
 */
const printHistory = () => {
	const style = document.createElement('style');
	style.media = 'print';
	style.textContent = PRINT_PAGE_RULE;
	document.head.appendChild(style);
	document.body.classList.add(PRINT_MODE_CLASS);
	const cleanup = () => {
		style.remove();
		document.body.classList.remove(PRINT_MODE_CLASS);
		window.removeEventListener('afterprint', cleanup);
	};
	window.addEventListener('afterprint', cleanup);
	window.print();
};

const PrintableHistory = ({
	data,
	formatLocation,
}: {
	data: MaterialLengthHistory;
	formatLocation: LocationLabelFormatter;
}) =>
	createPortal(
		<div className="material-history-print">
			<h1>Material History — {data.bar.friendly_id}</h1>
			<p>
				{data.material_name}
				{data.bar.deleted
					? ' · DELETED'
					: ` · Now ${formatInches(data.bar.current_length) ?? '—'} at ${
							formatLocation(data.bar.current_location) ?? 'no location'
						}`}
				{data.include_family && ` · Includes family of ${data.family_root_friendly_id}`}
			</p>
			<p>Central time · printed {dayjs().format('MMM D, YYYY h:mm A')}</p>
			<table>
				<thead>
					<tr>
						<th>When</th>
						<th>Bar</th>
						<th>Action</th>
						<th>Operator</th>
						<th>Length</th>
						<th>Location</th>
						<th>Job / notes</th>
					</tr>
				</thead>
				<tbody>
					{data.movements.map((row) => (
						<tr key={row.id}>
							<td>{formatShopTime(row.occurred_at, 'M/D/YY h:mm A')}</td>
							<td>{row.bar_friendly_id}</td>
							<td>{actionLabel(row).toUpperCase()}</td>
							<td>{operatorName(row)}</td>
							<td>{formatLengthChange(row)}</td>
							<td>{formatLocationChange(row, formatLocation)}</td>
							<td>
								{[row.order_line_item?.name, row.notes, row.is_backfilled && '(historical)']
									.filter(Boolean)
									.join(' · ')}
							</td>
						</tr>
					))}
				</tbody>
			</table>
		</div>,
		document.body
	);

const MaterialHistoryDrawer = ({
	materialLengthId,
	friendlyId,
	open,
	onClose,
}: MaterialHistoryDrawerProps) => {
	const [includeFamily, setIncludeFamily] = useState(false);
	const formatLocation = useLocationLabel();
	const { data, isFetching, isError, refetch } = useGetMaterialLengthHistoryQuery(
		{ id: materialLengthId ?? '', includeFamily },
		{ skip: !open || !materialLengthId, refetchOnMountOrArgChange: true }
	);
	const current = data && data.bar.id === materialLengthId ? data : undefined;
	const title = current?.bar.friendly_id ?? friendlyId ?? '';

	return (
		<Drawer
			open={open}
			onClose={onClose}
			width={560}
			destroyOnClose
			title={`Material History${title ? ` — ${title}` : ''}`}
			extra={
				<Button
					icon={<PrinterOutlined />}
					disabled={!current || isFetching || isError}
					onClick={printHistory}
				>
					Print
				</Button>
			}
		>
			<Space direction="vertical" size="middle" style={{ width: '100%' }}>
				{current && (
					<div>
						<Text type="secondary">{current.material_name}</Text>
						<div className="mt-1 flex flex-wrap items-center gap-2">
							{current.bar.deleted ? (
								<Tag>Deleted</Tag>
							) : (
								<>
									<Text strong>{formatInches(current.bar.current_length) ?? '—'}</Text>
									<Tag icon={<EnvironmentOutlined />} color="processing">
										{formatLocation(current.bar.current_location) ?? 'No location'}
									</Tag>
									{current.bar.status && <Tag>{current.bar.status}</Tag>}
								</>
							)}
						</div>
					</div>
				)}

				<Space>
					<Switch
						size="small"
						checked={includeFamily}
						onChange={setIncludeFamily}
						data-testid="material-history-include-family"
					/>
					<Text>Include related bars (split or cut from the same bar)</Text>
				</Space>

				{isError && (
					<Alert
						type="error"
						showIcon
						message="History could not be loaded."
						action={<Button onClick={() => refetch()}>Retry</Button>}
					/>
				)}

				{!current && isFetching && (
					<div className="flex justify-center py-8">
						<Spin />
					</div>
				)}

				{current && current.movements.length === 0 && (
					<Empty
						image={Empty.PRESENTED_IMAGE_SIMPLE}
						description="No logged movements for this bar yet."
					/>
				)}

				{current && current.movements.length > 0 && (
					<Spin spinning={isFetching}>
						<Timeline
							items={current.movements.map((row) => ({
								key: row.id,
								color: timelineColor(row.action),
								children: (
									<div data-testid="material-history-entry">
										<div className="flex flex-wrap items-center gap-2">
											<ActionTag row={row} />
											<Text type="secondary">{formatShopTime(row.occurred_at, 'MMM D, YYYY h:mm A')}</Text>
											{row.bar_friendly_id !== current.bar.friendly_id && (
												<Tag className="font-mono">{row.bar_friendly_id}</Tag>
											)}
											{row.is_backfilled && (
												<Tooltip title="Rebuilt from older records; from-location and length may be missing.">
													<Tag>historical</Tag>
												</Tooltip>
											)}
										</div>
										<div className="mt-1 text-sm">
											<div>
												{operatorName(row)}
												{row.recorded_by && (
													<Text type="secondary"> (logged in as {row.recorded_by.name})</Text>
												)}
											</div>
											<div>Length: {formatLengthChange(row)}</div>
											<div>Location: {formatLocationChange(row, formatLocation)}</div>
											{row.order_line_item && <div>Job: {row.order_line_item.name}</div>}
											{row.parent_bar_friendly_id && (
												<div>From bar {row.parent_bar_friendly_id}</div>
											)}
											{row.notes && <Text type="secondary">{row.notes}</Text>}
										</div>
									</div>
								),
							}))}
						/>
						{current.truncated && (
							<Text type="secondary" className="text-xs">
								Showing the most recent {current.movements.length} entries.
							</Text>
						)}
					</Spin>
				)}
			</Space>
			{open && current && <PrintableHistory data={current} formatLocation={formatLocation} />}
		</Drawer>
	);
};

export default MaterialHistoryDrawer;
